import React, { ReactNode, useState } from 'react';
import { ActivityIndicator, FlatList, Platform, Pressable, StyleSheet, View } from 'react-native';

import { EmptyState } from '~/components/core';
import { scaleSize } from '~/constants/scale';
import { useStyles } from '~/hooks';
import { Theme } from '~/theme';

import FromToSelector from '../../../Result/FromToSelector';

type Row = { id: string; timestamp: number };

type Props<T extends Row> = {
    data: T[];
    loading: boolean;
    onEndReached: () => void;
    renderRow: (item: T, isOpen: boolean) => ReactNode;
    renderDate?: (item: T, index: number) => ReactNode;
    setFrom: (d: Date) => void;
    setTo: (d: Date) => void;
    from: Date;
    to: Date;
    totalFormatted?: string;
    totalDirection?: string;
};

// Reuses the Deals rows and date picker without their screen wrapper, whose Loader is a
// full-screen dimming modal and would dim the whole app from inside a tab.
export default function LiveReportList<T extends Row>({
    data,
    loading,
    onEndReached,
    renderRow,
    renderDate,
    setFrom,
    setTo,
    from,
    to,
    totalFormatted,
    totalDirection,
}: Props<T>) {
    const styles = useStyles(stylesGenerator);
    const [openId, setOpenId] = useState('');

    return (
        <View style={styles.container}>
            {loading ? (
                <View style={styles.loading}>
                    <ActivityIndicator animating={true} size="small" />
                </View>
            ) : null}
            <FlatList
                style={styles.list}
                data={data}
                renderItem={({ item, index }) => (
                    <View key={item.id}>
                        {renderDate ? renderDate(item, index) : null}
                        <Pressable onPress={() => setOpenId(openId === item.id ? '' : item.id)}>
                            {renderRow(item, openId === item.id)}
                        </Pressable>
                    </View>
                )}
                keyExtractor={(item) => item.id}
                // Held back while the first page is in flight, so it doesn't flash before the rows.
                ListEmptyComponent={
                    loading ? null : (
                        <EmptyState title="ai-strategy-live-no-records" body="ai-strategy-live-no-records-body" />
                    )
                }
                initialNumToRender={20}
                onEndReachedThreshold={0.4}
                onEndReached={onEndReached}
            />
            {/* The selector seeds its own state from prevPeriod once. The tabs stay mounted, so
                without this key a range changed on one tab would leave the others' pickers
                showing the old dates while fetching the new ones. */}
            <FromToSelector
                key={`${from.getTime()}-${to.getTime()}`}
                setFromButton={setFrom}
                setToButton={setTo}
                prevPeriod={from}
                totalFormatted={totalFormatted}
                totalDirection={totalDirection}
            />
        </View>
    );
}

const stylesGenerator = (_theme: Theme) =>
    StyleSheet.create({
        container: { flex: 1 },
        list: {
            paddingTop: scaleSize(7),
            paddingLeft: scaleSize(7),
            paddingRight: Platform.OS === 'web' ? 0 : scaleSize(7),
        },
        loading: { paddingVertical: scaleSize(12), alignItems: 'center' },
    });
